import { motion } from "framer-motion";
import { ExternalLink, Phone, Mail, MapPin } from "lucide-react";
import { track, useViewEvent } from "@/lib/analytics";

const fadeUp = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.7 },
};

export function Chile() {
    const viewRef = useViewEvent("view_chile");
    return (
        <section
            id="chile"
            ref={viewRef}
            data-testid="chile-section"
            className="grain relative overflow-hidden bg-[#0A192F] py-28 md:py-40"
        >
            <img
                src="https://images.unsplash.com/photo-1631850033735-b4c7853b16df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxzYW50aWFnbyUyMGNoaWxlJTIwc2t5bGluZXxlbnwwfHx8fDE3ODY5MzgwODd8MA&ixlib=rb-4.1.0&q=85"
                alt="Santiago de Chile"
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover opacity-15"
            />
            <div className="absolute inset-0 bg-[#0A192F]/60" />
            <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2 lg:px-10">
                <motion.div {...fadeUp}>
                    <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 backdrop-blur-md">
                        <MapPin size={14} className="text-[#57A632]" />
                        <span className="text-xs font-bold tracking-[0.18em] text-white/80">DISPONIBILIDAD LOCAL</span>
                    </div>
                    <h2 className="font-display text-5xl font-extrabold tracking-tighter text-white sm:text-6xl lg:text-7xl" data-testid="chile-headline">
                        LYHER EN <span className="text-[#57A632]">CHILE</span>
                    </h2>
                    <p className="mt-6 max-w-md text-base leading-relaxed text-white/60 md:text-lg">
                        Encuentra el producto a través de nuestros canales comerciales en Chile.
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.92 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.9 }}
                    className="relative mx-auto w-full max-w-sm"
                    data-testid="chile-map"
                >
                    <svg viewBox="0 0 200 620" className="w-full drop-shadow-[0_0_35px_rgba(87,166,50,0.35)]">
                        <defs>
                            <linearGradient id="chileGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#1E293B" />
                                <stop offset="45%" stopColor="#57A632" stopOpacity="0.9" />
                                <stop offset="100%" stopColor="#1E293B" />
                            </linearGradient>
                        </defs>
                        <path
                            d="M96 12 C126 42 116 92 126 142 C136 202 108 262 118 332 C126 402 94 472 104 556 L90 604 C82 512 94 432 84 352 C74 272 96 202 86 132 C78 72 74 40 96 12 Z"
                            fill="url(#chileGrad)"
                            stroke="#57A632"
                            strokeOpacity="0.5"
                            strokeWidth="1.5"
                        />
                        <circle cx="98" cy="300" r="7" fill="#57A632" />
                        <circle cx="98" cy="300" r="7" fill="none" stroke="#57A632" strokeWidth="2" className="chile-pulse" />
                        <text x="116" y="295" fill="#ffffff" fontSize="13" fontWeight="700" fontFamily="Sora, sans-serif">
                            SANTIAGO
                        </text>
                        <text x="116" y="312" fill="#94a3b8" fontSize="10" fontFamily="Manrope, sans-serif">
                            Canales comerciales
                        </text>
                    </svg>
                </motion.div>
            </div>
        </section>
    );
}

function PartnerCard({ name, logo, tagline, desc, url, urlLabel, phone, phoneHref, email, events, testId, index, partner }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 44 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: index * 0.12 }}
            data-testid={testId}
            className="group relative overflow-hidden rounded-[2rem] border border-slate-100 bg-white p-8 shadow-[0_4px_24px_-6px_rgba(10,25,47,0.06)] transition-[box-shadow,transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-[#059669]/25 hover:shadow-[0_24px_60px_-18px_rgba(10,25,47,0.18)] md:p-12"
        >
            <div className="absolute right-0 top-0 h-1.5 w-24 rounded-bl-3xl bg-[#57A632]" />
            <p className="text-[11px] font-bold tracking-[0.28em] text-[#059669]">{tagline}</p>
            <div className="mt-5 flex h-14 items-center md:h-16">
                <img
                    src={logo}
                    alt={`Logo ${name}`}
                    data-testid={`${testId}-logo`}
                    className="max-h-full w-auto max-w-[240px] object-contain object-left transition-transform duration-300 group-hover:scale-[1.03]"
                />
            </div>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-500 sm:text-base">{desc}</p>

            <div className="mt-8 flex flex-wrap gap-3">
                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => track(events.visit, { partner, destination: "website", cta_location: "partners_section" })}
                    data-testid={`${testId}-visit`}
                    className="inline-flex items-center gap-2 rounded-full bg-[#0A192F] px-6 py-3.5 text-xs font-bold tracking-wider text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#059669]"
                >
                    <ExternalLink size={14} />
                    {urlLabel}
                </a>
                <a
                    href={phoneHref}
                    onClick={() => track(events.phone, { partner, destination: "phone", cta_location: "partners_section" })}
                    data-testid={`${testId}-call`}
                    className="inline-flex items-center gap-2 rounded-full border border-[#0A192F]/15 px-6 py-3.5 text-xs font-bold tracking-wider text-[#0A192F] transition-[border-color,color,transform] duration-300 hover:-translate-y-0.5 hover:border-[#059669] hover:text-[#059669]"
                >
                    <Phone size={14} />
                    LLAMAR
                </a>
                <a
                    href={`mailto:${email}`}
                    onClick={() => track(events.email, { partner, destination: "email", cta_location: "partners_section" })}
                    data-testid={`${testId}-email`}
                    className="inline-flex items-center gap-2 rounded-full border border-[#0A192F]/15 px-6 py-3.5 text-xs font-bold tracking-wider text-[#0A192F] transition-[border-color,color,transform] duration-300 hover:-translate-y-0.5 hover:border-[#059669] hover:text-[#059669]"
                >
                    <Mail size={14} />
                    ESCRIBIR
                </a>
            </div>

            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-2 border-t border-slate-100 pt-6 text-sm font-semibold text-slate-600">
                <span className="inline-flex items-center gap-2">
                    <Phone size={14} className="text-[#059669]" />
                    {phone}
                </span>
                <a href={`mailto:${email}`} className="inline-flex items-center gap-2 transition-colors duration-200 hover:text-[#059669]">
                    <Mail size={14} className="text-[#059669]" />
                    {email}
                </a>
            </div>
        </motion.div>
    );
}

export function Partners() {
    return (
        <section data-testid="partners-section" className="relative bg-[#F8FAFC] py-28 md:py-36">
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <motion.div {...fadeUp} className="max-w-2xl">
                    <p className="mb-5 text-[11px] font-bold tracking-[0.28em] text-[#059669]">
                        CANALES COMERCIALES EN CHILE
                    </p>
                    <h2 className="font-display text-4xl font-extrabold tracking-tighter text-[#0A192F] sm:text-5xl">
                        DÓNDE <span className="text-[#57A632]">ENCONTRARLO.</span>
                    </h2>
                </motion.div>

                <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-2">
                    <PartnerCard
                        index={0}
                        testId="partner-detex"
                        name="DETEX"
                        logo="/assets/logo-detex.png"
                        partner="detex"
                        tagline="CANAL COMERCIAL — CHILE"
                        desc="Soluciones de prevención, seguridad laboral y detección para organizaciones."
                        url="https://www.detex.cl"
                        urlLabel="VISITAR DETEX"
                        phone="+56 9 6727 3223"
                        phoneHref="tel:+56967273223"
                        email="ventas@detex.cl"
                        events={{ visit: "click_detex", phone: "click_phone_detex", email: "click_email_detex" }}
                    />
                    <PartnerCard
                        index={1}
                        testId="partner-cincosalud"
                        name="CINCO SALUD"
                        logo="/assets/logo-cincosalud.png"
                        partner="cincosalud"
                        tagline="CANAL COMERCIAL — CHILE"
                        desc="Soluciones de test rápidos y tecnología aplicada a salud y seguridad."
                        url="https://www.cincosalud.cl"
                        urlLabel="VISITAR CINCO SALUD"
                        phone="+56 9 9011 9887"
                        phoneHref="tel:+56990119887"
                        email="ventas@cincosalud.cl"
                        events={{ visit: "click_cincosalud", phone: "click_phone_cincosalud", email: "click_email_cincosalud" }}
                    />
                </div>
            </div>
        </section>
    );
}
